import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateInput from '../forms/dateInput';
import SignaturePad from 'signature_pad';
import SbrDrawer from '../sbr/SbrDrawer';
import { createPickupReceipt, getReceiptPrefill } from '../../services/documents';
import { tagLabel } from '../../constants/labels';

const ReceiptIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 1V2l-1 1-3-2-3 2-3-2-3 2-3-2z" /><line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="16" x2="12" y2="16" /></svg>
);

const emptyItem = () => ({ lineItemId: null, description: '', quantity: 1, tag: '', condition: '' });

const toDateInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Export the signature at a bounded size so the JSON body stays small: the
// pad is copied onto a 600px-wide canvas with a white background.
const exportSignature = (canvas) => {
    const width = 600;
    const height = Math.round((canvas.height / canvas.width) * width) || 200;
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(canvas, 0, 0, width, height);
    return out.toDataURL('image/png');
};

/**
 * Digital pickup receipt: the on-site checklist (items, tags, condition),
 * notes, expected return date, customer name and the customer's signature.
 * Mobile-first — the driver hands the phone to the customer to sign.
 *
 * Props: { show, onHide, jobId, client, onSaved(doc) }
 */
const PickupReceiptModal = ({ show, onHide, jobId, client, onSaved }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([emptyItem()]);
    const [notes, setNotes] = useState('');
    const [expectedReturnDate, setExpectedReturnDate] = useState('');
    const [signedByName, setSignedByName] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [hasSignature, setHasSignature] = useState(false);

    const canvasRef = useRef(null);
    const padRef = useRef(null);
    const wrapRef = useRef(null);

    const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(' ');

    // Prefill from the job's line items each time the drawer opens.
    useEffect(() => {
        if (!show || !jobId) return;
        let cancelled = false;
        setError('');
        setLoading(true);
        setHasSignature(false);
        getReceiptPrefill(jobId).then((response) => {
            if (cancelled) return;
            const prefill = response?.code === 200 ? response.message : null;
            const prefilled = (prefill?.items || []).map((i) => ({
                lineItemId: i.lineItemId || null,
                description: i.description || '',
                quantity: i.quantity ?? 1,
                tag: tagLabel(i.tag),
                condition: '',
            }));
            setItems(prefilled.length ? prefilled : [emptyItem()]);
            setExpectedReturnDate(toDateInput(prefill?.expectedReturnDate));
            setSignedByName(prefill?.customerName || clientName);
            setNotes('');
        }).catch(() => {
            if (cancelled) return;
            setItems([emptyItem()]);
            setSignedByName(clientName);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, jobId]);

    // Signature pad: size the canvas to its container (device pixel ratio
    // aware) and keep the strokes when the viewport changes.
    useEffect(() => {
        if (!show || loading) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const pad = new SignaturePad(canvas, { backgroundColor: 'rgba(255,255,255,0)', penColor: '#0B1424', minWidth: 0.8, maxWidth: 2.2 });
        padRef.current = pad;
        const onEnd = () => setHasSignature(!pad.isEmpty());
        pad.addEventListener('endStroke', onEnd);

        const resize = () => {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            const data = pad.toData();
            const width = wrapRef.current ? wrapRef.current.clientWidth : 320;
            canvas.width = width * ratio;
            canvas.height = 180 * ratio;
            canvas.style.width = `${width}px`;
            canvas.style.height = '180px';
            canvas.getContext('2d').scale(ratio, ratio);
            pad.clear();
            if (data.length) pad.fromData(data);
        };
        resize();
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
            pad.removeEventListener('endStroke', onEnd);
            pad.off();
            padRef.current = null;
        };
    }, [show, loading]);

    const updateItem = (index, patch) => setItems((list) => list.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    const removeItem = (index) => setItems((list) => (list.length > 1 ? list.filter((_, i) => i !== index) : list));
    const addItem = () => setItems((list) => [...list, emptyItem()]);

    const clearSignature = () => {
        padRef.current?.clear();
        setHasSignature(false);
    };

    const submit = async (event) => {
        event.preventDefault();
        // The modal is rendered from inside the job edit form, and a submit
        // event bubbles up the React tree even through a portal — without this
        // the outer <Form id="edit-job"> would save and close the job as soon
        // as the driver signs.
        event.stopPropagation();
        setError('');
        const valid = items.filter((i) => i.description.trim());
        if (!valid.length) { setError(t('receiptModal.errorNoItems')); return; }
        if (!signedByName.trim()) { setError(t('receiptModal.errorNoName')); return; }
        if (!padRef.current || padRef.current.isEmpty()) { setError(t('receiptModal.errorNoSignature')); return; }

        setSaving(true);
        try {
            const response = await createPickupReceipt(jobId, {
                items: valid.map((i) => ({
                    lineItemId: i.lineItemId || null,
                    description: i.description.trim(),
                    quantity: Number(i.quantity) || 0,
                    tag: i.tag || '',
                    condition: i.condition || '',
                })),
                notes: notes || null,
                expectedReturnDate: expectedReturnDate ? new Date(`${expectedReturnDate}T12:00:00`).toISOString() : null,
                signedByName: signedByName.trim(),
                signaturePng: exportSignature(canvasRef.current),
            });
            if (response?.code !== 200) throw new Error(typeof response?.message === 'string' ? response.message : t('receiptModal.saveError'));
            onSaved?.(response.message);
            onHide();
        } catch (e) {
            setError(e?.message || t('receiptModal.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <SbrDrawer show={show} onHide={onHide} secondary title={t('pickupReceipt.title')} subtitle={`${t('jobs.jobNumber', { id: jobId })}${clientName ? ` · ${clientName}` : ''}`} icon={<ReceiptIcon />}
            footer={<>
                <button type="button" className="btn-sbr btn-ghost-sbr" onClick={onHide} disabled={saving}>{t('common.cancel')}</button>
                <button type="submit" form="pickup-receipt-form" className="btn-sbr btn-primary-sbr" disabled={saving || loading}>{saving ? t('common.saving') : t('receiptModal.signAndGenerate')}</button>
            </>}>
            <form id="pickup-receipt-form" className="rcpt" onSubmit={submit}>
                {loading && <div className="rcpt-note">{t('receiptModal.loadingItems')}</div>}
                {error && <div className="rcpt-error">{error}</div>}

                <div className="rcpt-sec">{t('receiptModal.itemsPickedUp')}</div>
                <div className="rcpt-items">
                    {items.map((item, index) => (
                        <div className="rcpt-item" key={index}>
                            <div className="rcpt-item-head">
                                <span className="idx">{index + 1}</span>
                                <input className="desc" type="text" placeholder={t('receiptModal.itemDescription')} value={item.description} onChange={(e) => updateItem(index, { description: e.target.value })} />
                                <button type="button" className="rm" aria-label={t('orderForm.removeItem')} onClick={() => removeItem(index)} disabled={items.length === 1}>×</button>
                            </div>
                            <div className="rcpt-item-grid">
                                <label>{t('pickupReceipt.qty')}<input type="number" min="0" step="any" value={item.quantity} onChange={(e) => updateItem(index, { quantity: e.target.value })} /></label>
                                <label>{t('pickupReceipt.tag')}<input type="text" placeholder="26-00001" value={item.tag} onChange={(e) => updateItem(index, { tag: e.target.value })} /></label>
                                <label className="wide">{t('receiptModal.conditionOnPickup')}<input type="text" placeholder={t('receiptModal.conditionPlaceholder')} value={item.condition} onChange={(e) => updateItem(index, { condition: e.target.value })} /></label>
                            </div>
                        </div>
                    ))}
                </div>
                <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={addItem}>{t('receiptModal.addItem')}</button>

                <div className="rcpt-sec">{t('receiptModal.details')}</div>
                <div className="rcpt-grid">
                    <label>{t('pickupReceipt.expectedReturnDate')}<DateInput value={expectedReturnDate} onChange={(e) => setExpectedReturnDate(e.target.value)} /></label>
                    <label>{t('receiptModal.customerName')}<input type="text" value={signedByName} onChange={(e) => setSignedByName(e.target.value)} placeholder={t('receiptModal.whoIsSigning')} /></label>
                    <label className="wide">{t('common.notes')}<textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('receiptModal.agreedOnSite')} /></label>
                </div>

                <div className="rcpt-sec">{t('receiptModal.customerSignature')}</div>
                <div className="rcpt-sign" ref={wrapRef}>
                    <canvas ref={canvasRef} />
                    {!hasSignature && <span className="hint">{t('receiptModal.signHere')}</span>}
                </div>
                <div className="rcpt-sign-actions">
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={clearSignature}>{t('receiptModal.clear')}</button>
                    <small>{t('receiptModal.signatureDisclaimer')}</small>
                </div>
            </form>
        </SbrDrawer>
    );
};

export default PickupReceiptModal;
